import { Link } from "@tanstack/react-router";
import { ChevronRight } from "lucide-react";
import { Fragment } from "react";

const SITE_URL = "https://www.amr3designconstruction.com";

export type Crumb = {
  label: string;
  /** Absolute in-app path, e.g. "/about". Omit for the current page. */
  to?: string;
};

/**
 * Minimal, accessible breadcrumb trail for secondary pages.
 * Renders visible navigation plus a BreadcrumbList JSON-LD block for SEO.
 * Designed to sit inside a dark hero above the main title.
 */
export function Breadcrumbs({
  items,
  className = "",
}: {
  items: Crumb[];
  className?: string;
}) {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.label,
      ...(item.to ? { item: `${SITE_URL}${item.to}` } : {}),
    })),
  };

  return (
    <nav
      aria-label="Breadcrumb"
      className={`mb-4 flex flex-wrap items-center gap-1.5 text-sm text-white/70 ${className}`}
    >
      {items.map((item, i) => {
        const isLast = i === items.length - 1;
        return (
          <Fragment key={`${item.label}-${i}`}>
            {item.to && !isLast ? (
              <Link
                to={item.to}
                className="transition-colors hover:text-accent"
              >
                {item.label}
              </Link>
            ) : (
              <span
                className={isLast ? "font-medium text-white" : undefined}
                aria-current={isLast ? "page" : undefined}
              >
                {item.label}
              </span>
            )}
            {!isLast && (
              <ChevronRight className="h-3.5 w-3.5 shrink-0 text-white/40" aria-hidden />
            )}
          </Fragment>
        );
      })}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
    </nav>
  );
}
